import { useState } from "react";

import { BsMetaUtil, StrategyExpandedInfo, useLoopscaleWalletContext } from "@bridgesplit/abf-react";
import { Column, StatColumn, TagTextAlert } from "@bridgesplit/ui";
import { bsMath, formatAddress } from "@bridgesplit/utils";
import { TransactionTypes } from "app/types";

import { AppButton, useBalanceChecker, TokenInput, PausedExternalYieldAlert } from "../../common";
import { useStrategyDeposit } from "./util";

export default function StrategyDeposit({ strategy }: { strategy: StrategyExpandedInfo | undefined }) {
    const {
        data: { userWalletAddress: activeWallet }
    } = useLoopscaleWalletContext();

    const [amount, setAmount] = useState<number>();

    const mint = strategy?.strategy.principalMint;
    const { balance, insufficientBalance, isLoading } = useBalanceChecker({
        mint,
        amount
    });
    const metadata = strategy?.principalMetadata;
    const symbol = BsMetaUtil.getSymbol(metadata);

    const deposit = useStrategyDeposit();

    return (
        <>
            <TokenInput
                label="Amount"
                symbol={symbol}
                maxAmount={balance}
                value={amount}
                setValue={setAmount}
                metadata={metadata}
                maxLoading={isLoading}
            />
            {!strategy?.strategy?.originationsEnabled && amount && (
                <TagTextAlert icon="tooltip" color="info">
                    This deposit won't be used until you re-enable new offers in settings
                </TagTextAlert>
            )}
            <StatColumn
                stats={[
                    {
                        caption: "Deposit from",
                        value: formatAddress(activeWallet)
                    },
                    {
                        caption: "Position balance",
                        symbol,
                        value: [
                            strategy?.totalBalance,
                            Math.max(bsMath.add(strategy?.totalBalance, amount || 0) || 0, 0)
                        ],
                        hide: !amount
                    }
                ]}
            />

            <Column spacing={0.5}>
                <PausedExternalYieldAlert
                    variant="strategy"
                    source={strategy?.externalYieldInfo?.externalYieldSource}
                />
                <AppButton
                    isTransaction
                    transactionType={TransactionTypes.StrategyDeposit}
                    fullWidth
                    disabled={!amount || insufficientBalance}
                    asyncCta={{
                        options: { onSuccess: () => setAmount(undefined) },
                        onClickWithResult: () => deposit({ amount, strategy })
                    }}
                    variant="contained"
                >
                    Deposit
                </AppButton>
            </Column>
        </>
    );
}
